/**
 * A context menu at a point on the page (1.7.3: the timeline's clips, gaps and graphics). Keyboard accessible: it
 * takes the focus when it opens (the first enabled item), ↑ ↓ Home End move between the items, Enter or Space picks
 * one, Escape or Tab closes it and gives the focus back to where it was; a click outside, a scroll or a resize closes
 * it. Placed on the page (fixed) and kept inside the window.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as RKeyboardEvent, type ReactNode } from 'react';

export type ContextMenuEntry = { key: string; label: ReactNode; keys?: string; onSelect: () => void; disabled?: boolean; danger?: boolean } | 'separator';

export function ContextMenu({ at, items, onClose, label = 'Actions', testId = 'context-menu' }: {
  at: { x: number; y: number };
  items: ContextMenuEntry[];
  onClose: () => void;
  label?: string;
  testId?: string;
}) {
  const menu = useRef<HTMLDivElement>(null);
  const returnTo = useRef<Element | null>(typeof document !== 'undefined' ? document.activeElement : null);
  const [pos, setPos] = useState(at);
  const close = useRef(onClose);
  close.current = onClose;

  const buttons = () => [...(menu.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)') ?? [])];
  const done = (refocus = true) => {
    close.current();
    if (refocus && returnTo.current instanceof HTMLElement) returnTo.current.focus({ preventScroll: true });
  };

  // Inside the window, then the focus on the first item.
  useLayoutEffect(() => {
    const el = menu.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.max(4, Math.min(at.x, window.innerWidth - r.width - 4));
    const y = Math.max(4, Math.min(at.y, window.innerHeight - r.height - 4));
    if (x !== pos.x || y !== pos.y) setPos({ x, y });
    buttons()[0]?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at.x, at.y]);

  useEffect(() => {
    const outside = (e: PointerEvent) => { if (!menu.current?.contains(e.target as Node)) done(false); };
    const away = () => done(false);
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    window.addEventListener('blur', away);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
      window.removeEventListener('blur', away);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e: RKeyboardEvent) => {
    // The menu's keys are its own: the editor's shortcuts do not see them.
    e.stopPropagation();
    const list = buttons();
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    const go = (k: number) => { e.preventDefault(); list[(k + list.length) % list.length]?.focus(); };
    if (e.key === 'ArrowDown') go(i + 1);
    else if (e.key === 'ArrowUp') go(i < 0 ? list.length - 1 : i - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(list.length - 1);
    else if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); done(); }
  };

  return (
    <div ref={menu} className="ctx-menu" role="menu" aria-label={label} data-testid={testId} style={{ left: pos.x, top: pos.y }} onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}>
      {items.map((it, k) => (it === 'separator'
        ? <div key={`sep-${k}`} className="ctx-sep" role="separator" />
        : (
          <button key={it.key} type="button" role="menuitem" className={`ctx-item${it.danger ? ' danger' : ''}`} disabled={it.disabled} data-action={it.key}
            onClick={() => { done(); it.onSelect(); }}>
            <span className="ctx-label">{it.label}</span>
            {it.keys && <kbd className="ctx-keys" aria-hidden>{it.keys}</kbd>}
          </button>
        )))}
    </div>
  );
}
